import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getSocket } from '../socket';
import { playSound } from '../utils/soundAlerts';
import API from '../api';

// ── 업무요청 실시간 알림 모달 ────────────────────────────────────────
// 업무관리(Tasks.js)에서 누군가 나에게 업무요청을 등록하면(backend/routes/tasks.js의
// notifyNewTaskRequest가 'task:request:new' 이벤트를 내 개인 소켓 room(user:<아이디>)으로
// 쏴준다), 지금 어느 화면을 보고 있든 바로 모달로 알려준다.
// 단, 생산전광판(/display)을 전체화면으로 띄워놓고 보고 있는 동안은 방해가 되므로 예외로 둔다
// (AutoLogout.js와 마찬가지로 document.fullscreenElement로 판단 — 이 앱에서 전체화면 버튼은
// 생산전광판 화면에만 있으므로 이 값이 켜져 있으면 곧 생산전광판을 전체화면으로 보고 있다는 뜻).
export default function TaskRequestAlert() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [incoming, setIncoming] = useState(null); // 새로 들어온 업무요청/대응 알림 1건(없으면 null)
  const [kind, setKind] = useState('new');         // 'new'(새 요청) | 'responded'(대응사항 입력됨)
  // "보류"를 누르면 지금 당장은 닫되, 나중에(SNOOZE_MS 후) 같은 알림을 다시 띄워준다("나중에 확인"
  // 개념). 그 사이에 같은 요청에 대해 새로운 실시간 알림이 오면(예: 상대가 또 대응함) 예약된
  // 재알림은 취소하고 최신 알림으로 대체한다.
  const snoozeTimerRef = useRef(null);
  const SNOOZE_MS = 10 * 60 * 1000; // 10분 뒤 다시 알림

  const clearSnoozeTimer = () => {
    if (snoozeTimerRef.current) {
      clearTimeout(snoozeTimerRef.current);
      snoozeTimerRef.current = null;
    }
  };

  useEffect(() => {
    if (!user) return;
    const socket = getSocket();
    if (!socket) return;

    const onNew = (payload) => {
      if (document.fullscreenElement) return; // 생산전광판 전체화면 중에는 방해하지 않음
      clearSnoozeTimer();
      setKind('new');
      setIncoming(payload);
      playSound('task');
    };
    // 요청받은 쪽이 대응사항을 입력(대기/보류/확인요청 중 무엇이든)하면 요청한 사람에게도
    // 동일한 방식으로 실시간 모달 알림을 띄운다 — tasks.js의 notifyRequestResponded가 쏨.
    const onResponded = (payload) => {
      if (document.fullscreenElement) return;
      clearSnoozeTimer();
      setKind('responded');
      setIncoming(payload);
      playSound('task');
    };
    socket.on('task:request:new', onNew);
    socket.on('task:request:responded', onResponded);
    return () => {
      socket.off('task:request:new', onNew);
      socket.off('task:request:responded', onResponded);
      clearSnoozeTimer();
    };
  }, [user]);

  if (!incoming) return null;

  const goToTasks = () => {
    clearSnoozeTimer();
    setIncoming(null);
    // 이미 업무관리 화면이면 그대로, 아니면 이동 — 요청내역 모달을 자동으로 열어주기 위해
    // state로 표시만 남기고, 실제로 여는 처리는 Tasks.js에서 담당한다.
    navigate('/tasks', { state: { openTaskRequests: true } });
  };

  // 확인 — 이 알림을 "확인했다"는 뜻으로 닫는다. 업무관리로 이동하지 않고 그냥 닫을 뿐이지만,
  // 상세를 열어본 것과 동일하게 seen_map에 내 확인시각을 남겨서 업무요청 버튼의 숫자 배지도
  // 함께 사라지게 한다(요청내역 모달에서 상세를 열었을 때와 같은 API를 재사용).
  const handleConfirm = () => {
    clearSnoozeTimer();
    if (incoming?.id) API.patch(`/tasks/requests/${incoming.id}/seen`).catch(() => {});
    setIncoming(null);
  };

  // 보류 — "나중에 확인한다": 지금은 모달을 닫고, SNOOZE_MS 뒤에 동일한 알림을 다시 띄운다.
  const holdForLater = () => {
    const payload = incoming;
    const savedKind = kind;
    setIncoming(null);
    clearSnoozeTimer();
    snoozeTimerRef.current = setTimeout(() => {
      snoozeTimerRef.current = null;
      if (document.fullscreenElement) return;
      setKind(savedKind);
      setIncoming(payload);
      playSound('task');
    }, SNOOZE_MS);
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:3000,
      display:'flex', alignItems:'center', justifyContent:'center' }}
      onClick={() => setIncoming(null)}>
      <div style={{ background:'#fff', borderRadius:14, width:420, maxWidth:'90vw', padding:'26px 24px 22px' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ fontSize:12.5, fontWeight:700, color:'#0F766E', marginBottom:8 }}>
          {kind === 'responded' ? '💬 업무요청에 대응사항이 등록되었습니다' : '📨 새 업무요청이 도착했습니다'}
        </div>
        <div style={{ fontSize:16.5, fontWeight:700, color:'#1a1a1a', marginBottom:8 }}>{incoming.title}</div>
        {kind === 'responded' ? (
          <div style={{ fontSize:13, color:'#666', marginBottom:4 }}>
            대응자 {incoming.by}
          </div>
        ) : (
          <div style={{ fontSize:13, color:'#666', marginBottom:4 }}>
            요청부서 {incoming.request_department || '-'} · 요청자 {incoming.created_by}
          </div>
        )}
        {incoming.due_date && (
          <div style={{ fontSize:13, color:'#dc2626', fontWeight:600, marginBottom:4 }}>완료일 {incoming.due_date}</div>
        )}
        {(kind === 'responded' ? incoming.response_content : incoming.content) && (
          <div style={{ marginTop:10, padding:'10px 12px', background:'#f8fafc', borderRadius:8,
            fontSize:13, color:'#333', whiteSpace:'pre-wrap', lineHeight:1.6, maxHeight:120, overflowY:'auto' }}>
            {kind === 'responded' ? incoming.response_content : incoming.content}
          </div>
        )}
        <div style={{ display:'flex', gap:8, marginTop:20 }}>
          <button onClick={goToTasks}
            style={{ flex:1, padding:'11px', background:'#0F766E', color:'#fff', border:'none',
              borderRadius:8, fontSize:13, fontWeight:700, cursor:'pointer' }}>
            업무관리로 이동
          </button>
          <button onClick={holdForLater} title="10분 뒤 다시 알려드립니다"
            style={{ flex:1, padding:'11px', background:'#fff', color:'#b45309', border:'1px solid #fbbf24',
              borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer' }}>
            보류
          </button>
          <button onClick={handleConfirm}
            style={{ flex:1, padding:'11px', background:'#fff', color:'#555', border:'1px solid #ddd',
              borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer' }}>
            확인
          </button>
        </div>
      </div>
    </div>
  );
}